import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { recommendationsAPI } from '../utils/api';
import AuthenticatedImage from './AuthenticatedImage';
import RefreshIcon from '@mui/icons-material/RefreshRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardRounded';
import ThumbUpIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDownAltOutlined';
import '../styles/RecommendationCards.css';

const DEFAULT_RECS = [
  {
    rec_type: 'occasion',
    title: 'Build a look from your closet',
    description: 'Ask the stylist to combine pieces you already own for a specific occasion.',
    starter_message: 'Build a grounded outfit from my closet for today.',
    rationale: 'This starter remains available while personalized cards reconnect.',
    status: 'fallback',
    confidence: 0.6,
    image_url: '/images/samples/whiteDressShirt.png',
  },
  {
    rec_type: 'trend',
    title: 'Refresh an everyday favorite',
    description: 'Create two variations around a piece you wear often.',
    starter_message: 'Help me create two new combinations around an everyday favorite.',
    rationale: 'A useful fallback that does not depend on generated imagery.',
    status: 'fallback',
    confidence: 0.6,
    image_url: '/images/samples/blueBlazer.png',
  },
  {
    rec_type: 'closet_gap',
    title: 'Find the most useful closet gap',
    description: 'Identify one addition that would unlock the most combinations.',
    starter_message: 'Analyze my closet and explain the single most useful gap.',
    rationale: 'Your saved closet remains the source of truth for the analysis.',
    status: 'fallback',
    confidence: 0.6,
    image_url: '/images/samples/brownLeatherShoes.png',
  },
];

const TYPE_LABELS = {
  occasion: 'Occasion',
  trend: 'Trending',
  closet_gap: 'Closet Gap',
};

const describeRecommendationFailure = (err) => {
  if (err?.code === 'ECONNABORTED') {
    return 'The recommendation service is waking up. These closet-grounded starters are ready while it reconnects.';
  }
  if (err?.code === 'ERR_NETWORK') {
    return 'FashionMate cannot reach the recommendation service. These closet-grounded starters remain available.';
  }
  return 'Personalized cards are temporarily unavailable. These closet-grounded starters are ready now.';
};

const RecommendationCards = () => {
  const navigate = useNavigate();
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [feedbackTarget, setFeedbackTarget] = useState(null);
  const retryTimerRef = useRef(null);

  const fetchRecs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await recommendationsAPI.getHome();
      setRecs(response?.data?.data || []);
    } catch (err) {
      setRecs(DEFAULT_RECS);
      setError(describeRecommendationFailure(err));
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = setTimeout(async () => {
        try {
          const retryResponse = await recommendationsAPI.getHome();
          setRecs(retryResponse?.data?.data || DEFAULT_RECS);
          setError(null);
        } catch (retryError) {
          setError(describeRecommendationFailure(retryError));
        }
      }, 8000);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecs();
    return () => clearTimeout(retryTimerRef.current);
  }, [fetchRecs]);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const response = await recommendationsAPI.refresh();
      setRecs(response?.data?.data || []);
      setError(null);
    } catch (err) {
      setError(`${describeRecommendationFailure(err)} Your current recommendations are still shown.`);
    } finally {
      setRefreshing(false);
    }
  };

  const handleFeedback = async (id, action, reason = null) => {
    if (!id) return;
    try {
      await recommendationsAPI.feedback(id, action, reason);
      setRecs((prev) =>
        prev.map((r) => (r.id === id ? { ...r, feedback: action, feedback_reason: reason } : r))
      );
      setFeedbackTarget(null);
    } catch (err) {
      setError('FashionMate could not save that feedback. Your recommendations are unchanged.');
    }
  };

  const handleOpenChat = (rec) => {
    if (rec.id) navigate(`/chat?rec=${rec.id}`);
    else navigate(`/chat?prompt=${encodeURIComponent(rec.starter_message)}`);
  };

  if (loading) {
    return (
      <section className="rec-section">
        <div className="rec-header">
          <h3>Recommended for You</h3>
        </div>
        <div className="rec-grid">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rec-skeleton glass-panel">
              <div className="rec-skeleton-image" />
              <div className="rec-skeleton-body">
                <div className="rec-skeleton-line short" />
                <div className="rec-skeleton-line wide" />
                <div className="rec-skeleton-line medium" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (recs.length === 0) return null;

  return (
    <section className="rec-section">
      <div className="rec-header">
        <div>
          <h3>Recommended for You</h3>
          <p className="rec-section-subtitle">Grounded in your closet, preferences, and feedback.</p>
        </div>
        <button className="rec-refresh-btn" onClick={handleRefresh} disabled={refreshing}>
          <RefreshIcon /> {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error && <p className="rec-notice" role="status">{error}</p>}

      <div className="rec-grid">
        {recs.map((rec) => (
          <div key={rec.id} className="rec-card glass-panel">
            <div className="rec-card-image">
              <span className={`rec-type-badge ${rec.rec_type}`}>
                {TYPE_LABELS[rec.rec_type] || rec.rec_type}
              </span>
              <AuthenticatedImage
                src={rec.image_url}
                alt={rec.title}
              />
            </div>

            <div className="rec-card-body">
              <div className="rec-card-status">
                <span>{rec.status === 'fallback' ? 'Starter' : 'Closet grounded'}</span>
                <span>{Math.round((rec.confidence || 0.5) * 100)}% confidence</span>
              </div>
              <h4>{rec.title}</h4>
              <p>{rec.description}</p>
              {rec.rationale && <p className="rec-rationale"><strong>Why this:</strong> {rec.rationale}</p>}

              <div className="rec-card-actions">
                <button className="rec-cta" onClick={() => handleOpenChat(rec)}>
                  Open in Chat <ArrowForwardIcon />
                </button>

                <div className="rec-feedback">
                  <button
                    className={rec.feedback === 'like' ? 'active' : ''}
                    onClick={() => handleFeedback(rec.id, 'like', 'more_like_this')}
                    aria-label="More like this"
                    title="More like this"
                  >
                    <ThumbUpIcon />
                  </button>
                  <button
                    className={rec.feedback === 'dislike' ? 'active' : ''}
                    onClick={() => rec.id && setFeedbackTarget(feedbackTarget === rec.id ? null : rec.id)}
                    aria-label="Not for me"
                    title="Not for me"
                  >
                    <ThumbDownIcon />
                  </button>
                </div>
              </div>

              {feedbackTarget === rec.id && (
                <div className="rec-feedback-reasons" role="group" aria-label="Why is this not for you?">
                  <button onClick={() => handleFeedback(rec.id, 'dislike', 'wrong_occasion')}>Wrong occasion</button>
                  <button onClick={() => handleFeedback(rec.id, 'dislike', 'not_my_style')}>Not my style</button>
                  <button onClick={() => handleFeedback(rec.id, 'dislike', 'already_wore')}>Already wore this</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default RecommendationCards;
